import { IntegrationCard } from '@components'

import { Flex, ModalConfirmation, notify, Text, useModal } from '@billy/design-system'
import groupBy from 'lodash/groupBy'
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useMarkPurchasesStepDone } from '@features/quickStart/hooks'
import { ScoutConnectionImapModal } from '@views/receipts/elements/ScoutConnectionImapModal'

import { queryClient } from '../../../../../../config/queryClient'
import { ModalId } from '../../../../../../enums/modalId'
import { QueryKeys } from '../../../../../../enums/queryKeys'
import { ScoutProvider } from '../../../../enums/scoutProvider'
import { useDeleteScoutConnection } from '../../../../hooks/useDeleteScoutConnection'
import { useFetchScoutConnections } from '../../../../hooks/useFetchScoutConnections'
import { ScoutConnection } from '../../../../types/scoutConnection'
import { deleteProviderModalTranslationKeys } from '../../constants/providerTranslationKey'
import { ScoutConnectionCard } from '../ScoutConnectionCard'
import * as Styled from './styles'

export interface ScoutConnectionsProps {
  onConnectionDeleted?: () => void
}

export const ScoutConnections = ({ onConnectionDeleted }: ScoutConnectionsProps): ReactElement => {
  const [toBeDeletedConnection, setToBeDeletedConnection] = useState<ScoutConnection>()

  const { t } = useTranslation()
  const markPurchasesStepDone = useMarkPurchasesStepDone()

  const { connections } = useFetchScoutConnections({
    onScoutProcessingEnd: () => {
      queryClient.invalidateQueries({ queryKey: QueryKeys.AttachmentsList })
      queryClient.invalidateQueries({ queryKey: QueryKeys.AttachmentsCount })
    },
    shouldRefetch: true,
  })

  const { activeProviders = [], inactiveProviders = [] } = useMemo(
    () =>
      groupBy([ScoutProvider.Google, ScoutProvider.Microsoft, ScoutProvider.Imap], (provider) =>
        connections?.find((connection) => connection.provider === provider) ? 'activeProviders' : 'inactiveProviders',
      ),
    [connections],
  )

  useEffect(() => {
    if (activeProviders.length > 0) {
      markPurchasesStepDone()
    }
  }, [activeProviders.length, markPurchasesStepDone])

  const { close: closeDeleteConfirmationModal } = useModal(ModalId.ScoutConnectionDeletionModal)

  const { delete: deleteConnection, isProcessing: isDeleting } = useDeleteScoutConnection({
    onSuccess: (connection: ScoutConnection) => {
      notify({
        id: 'connection-deleted',
        message: t(deleteProviderModalTranslationKeys[connection.provider].succeeded),
        variant: 'success',
      })
      closeDeleteConfirmationModal()
    },
    onError: (_, connection: ScoutConnection) => {
      notify({
        id: 'connection-deleted',
        message: t(deleteProviderModalTranslationKeys[connection.provider].failed),
        variant: 'error',
      })
    },
  })

  const handleConnectionDeleted = useCallback(
    (connection: ScoutConnection) => {
      onConnectionDeleted?.()
      setToBeDeletedConnection(connection)
    },
    [onConnectionDeleted],
  )

  const handleDeleteModalOkClick = useCallback(() => {
    toBeDeletedConnection && deleteConnection(toBeDeletedConnection)
  }, [deleteConnection, toBeDeletedConnection])

  const renderConnections = useCallback(
    (providers: ScoutProvider[]) =>
      providers.map((provider) => {
        const connection = connections?.find((connection) => connection.provider === provider)

        return (
          <ScoutConnectionCard
            key={provider}
            provider={provider}
            connection={connection}
            onConnectionDeleted={handleConnectionDeleted}
          />
        )
      }),
    [connections, handleConnectionDeleted],
  )

  return (
    <Styled.ScoutConnectionsWrapper>
      <Flex flexDirection="column">
        {activeProviders?.length > 0 && (
          <Text variant="bodySM" colorVariant="secondary" uppercase>
            {t('receipts.side_panel.receipt_finder.status.active')}
          </Text>
        )}
        {renderConnections(activeProviders)}
        {inactiveProviders?.length > 0 && (
          <>
            {activeProviders?.length > 0 && <IntegrationCard.Divider />}
            <Text variant="bodySM" colorVariant="secondary" uppercase>
              {t('receipts.side_panel.receipt_finder.status.inactive')}
            </Text>
          </>
        )}
        {renderConnections(inactiveProviders)}

        <ScoutConnectionImapModal id={ModalId.ScoutConnectionImapModal} />

        {/* Delete Confirmation Modal  */}
        {toBeDeletedConnection && (
          <ModalConfirmation
            cancelLabel={t('cancel')}
            danger
            id={ModalId.ScoutConnectionDeletionModal}
            message={t(deleteProviderModalTranslationKeys[toBeDeletedConnection.provider].description)}
            okLabel={t('yes')}
            okLoading={isDeleting}
            onOk={handleDeleteModalOkClick}
            title={t(deleteProviderModalTranslationKeys[toBeDeletedConnection.provider].title)}
          />
        )}
      </Flex>
    </Styled.ScoutConnectionsWrapper>
  )
}
